import {CAMPAIGN_KEYS} from "@/api/campaignApi";
import {MEDIA_KEYS} from "@/api/mediaApi";
import InstagramPartnershipAdsIcon from "@/assets/colorable/instagram_partnership_ads.svg?react";
import TiktokSparkAdsIcon from "@/assets/colorable/tiktok_spark_ads.svg?react";
import CopyToClipboardButton from "@/components/Common/CopyToClipboardButton";
import {client} from "@/httpClient";
import clsxm from "@/lib/clsxm";
import {Can} from "@/permissions";
import {capturePostHogEvent, PostHogEvents} from "@/posthog";
import {queryClient} from "@/queryClient";
import useCampaignInfluencerFilterStore from "@/store/campaignInfluencerFilter";
import useCampaignManagementStore, {useManagementCampaign} from "@/store/campaignManagement";
import type {TContent, TMediaGalleryItem, TPaginated} from "@/store/types";
import {useMutation, type InfiniteData} from "@tanstack/react-query";
import type {AxiosResponse} from "axios";
import {produce} from "immer";
import {type FC, type ReactNode} from "react";
import {useTranslation} from "react-i18next";

type TBoostContentType = TContent | "TIKTOK_VIDEO";

const CONTENT_TYPE_LABEL_KEYS: Partial<Record<TBoostContentType, "post" | "reel" | "tiktok">> = {
  FEED: "post",
  POST: "post",
  REEL: "reel",
  REELS: "reel",
  TIKTOK: "tiktok",
  TIKTOK_VIDEO: "tiktok",
};

const BoostVideoButton: FC<{
  buttonStyle?: "Gradient" | "Blue" | "White" | "Custom";
  campaignId?: number | null;
  className?: string;
  contentType?: TBoostContentType | null;
  icon?: ReactNode;
  iconClassName?: string;
  id: number;
  label?: string;
  lastCopied?: string | null;
  shouldKeepStyleOnSuccess?: boolean;
  videoCode: string;
}> = ({
  buttonStyle,
  campaignId,
  className,
  contentType,
  icon,
  iconClassName,
  id,
  label,
  lastCopied,
  shouldKeepStyleOnSuccess,
  videoCode,
}) => {
  const {t} = useTranslation(["common", "campaign_list"]);
  const campaign = useManagementCampaign();
  const dateFilter = useCampaignManagementStore(state => state.reportingDateFilter);
  const ordering = useCampaignManagementStore(state => state.reportingOrdering);
  const searchParam = useCampaignInfluencerFilterStore(state => state.filters.search);
  const resolvedCampaignId = campaignId ?? campaign.id;

  const isTikTok = contentType === "TIKTOK" || contentType === "TIKTOK_VIDEO" || !!campaign.tiktok;
  const copiedMediaType = isTikTok ? "TIKTOK" : "INSTAGRAM";
  const mutationKey = isTikTok ? MEDIA_KEYS.mTikTokCodeCopy(id) : MEDIA_KEYS.mInstagramCodeCopy(id);
  const contentTypeLabelKey = contentType ? CONTENT_TYPE_LABEL_KEYS[contentType] : undefined;

  const {mutate: copyCode} = useMutation({
    meta: {skipSuccessToast: true},
    mutationKey: mutationKey,
    mutationFn: () => client.post(mutationKey[0]),
    onSuccess: () => {
      capturePostHogEvent(PostHogEvents.AdCodeCopied, {
        adType: isTikTok ? "spark_ad" : "partnership_ad",
        campaignId: resolvedCampaignId,
        contentId: id,
      });

      if (!resolvedCampaignId) {
        return;
      }

      const copiedAt = new Date().toISOString();

      queryClient.setQueriesData<InfiniteData<AxiosResponse<TPaginated<TMediaGalleryItem>>>>(
        {predicate: query => String(query.queryKey[0]).startsWith(`/campaign/${resolvedCampaignId}/media_gallery/`)},
        previousState =>
          previousState &&
          produce(previousState, draft => {
            draft.pages.forEach(page =>
              page.data.results.forEach(item => {
                if (item.type === copiedMediaType && item.result.id === id) {
                  item.result.last_copied = copiedAt;
                }
              }),
            );
          }),
      );

      queryClient.setQueriesData<InfiniteData<AxiosResponse<TPaginated<{id: number; last_copied?: string | null}>>>>(
        {
          predicate: query =>
            String(query.queryKey[0]).startsWith("/media_library/?") &&
            String(query.queryKey[0]).includes(`content_type=${copiedMediaType}`),
        },
        previousState =>
          previousState &&
          produce(previousState, draft => {
            draft.pages.forEach(page =>
              page.data.results.forEach(media => {
                if (media.id === id) {
                  media.last_copied = copiedAt;
                }
              }),
            );
          }),
      );

      [
        CAMPAIGN_KEYS.qCampaignActiveApplications(resolvedCampaignId, searchParam),
        CAMPAIGN_KEYS.qCampaignReportingApplications({
          campaignId: resolvedCampaignId,
          dateFilter,
          ordering,
          searchParam,
        }),
        CAMPAIGN_KEYS.qCampaignFeaturedContent(resolvedCampaignId, dateFilter),
      ].map(queryKey => queryClient.invalidateQueries({queryKey}));
    },
  });

  return (
    <Can I="read" a="ContentDetails">
      <CopyToClipboardButton
        buttonStyle={buttonStyle}
        className={clsxm("h-12", className)}
        contentToCopy={videoCode}
        icon={
          icon ??
          (isTikTok ? (
            <TiktokSparkAdsIcon className={clsxm("text-primary-dark-solid h-4 w-3 shrink-0", iconClassName)} />
          ) : (
            <InstagramPartnershipAdsIcon
              className={clsxm("text-primary-dark-solid h-3.5 w-4.5 shrink-0", iconClassName)}
            />
          ))
        }
        label={
          lastCopied
            ? t("copyCodeAgain")
            : (label ??
              (contentTypeLabelKey
                ? t("campaign_list:boost_content", {contentType: t(contentTypeLabelKey)})
                : t("campaign_list:boost_video")))
        }
        onCopyCallback={() =>
          setTimeout(() => {
            copyCode();
          }, 1000)
        }
        shouldKeepStyleOnSuccess={shouldKeepStyleOnSuccess}
        successLabel={t(lastCopied ? "copyCodeAgain" : "copied")}
      />
    </Can>
  );
};

export default BoostVideoButton;
